import React, { useEffect, useState } from 'react';
import { Loader2, Save, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getErrorLogSettings, updateErrorLogSettings } from '@/services/grc';

const PRESETS = [30, 90, 180, 365, 730, 1095, 1825, 2190];
const label = (d) => (d % 365 === 0 ? `${d / 365} year${d === 365 ? '' : 's'}` : `${d} days`);

const ErrorRetentionSettings = ({ tenantKey }) => {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getErrorLogSettings()
      .then((d) => { setCfg(d); setDays(String(d.error_retention_days)); })
      .catch(() => setCfg({ can_edit: false }));
  }, [tenantKey]);

  if (!cfg) return null;

  const save = async () => {
    const n = parseInt(days, 10);
    if (!n || n < 1 || n > (cfg.max_retention_days || 2190)) {
      toast.error('Retention must be between 1 day and 6 years (2190 days)');
      return;
    }
    setSaving(true);
    try {
      const d = await updateErrorLogSettings({ error_retention_days: n });
      setCfg(d);
      toast.success(`Error logs will be kept for ${label(n)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save retention');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="error-retention-settings">
      <Timer className="w-4 h-4 text-gray-500" />
      <span className="text-gray-600">Keep error logs for</span>
      {cfg.can_edit ? (
        <>
          <Select value={PRESETS.includes(Number(days)) ? days : 'custom'}
            onValueChange={(v) => v !== 'custom' && setDays(v)}>
            <SelectTrigger className="w-[130px] h-9" data-testid="error-retention-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESETS.map((d) => <SelectItem key={d} value={String(d)}>{label(d)}</SelectItem>)}
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min={1} max={2190} value={days} onChange={(e) => setDays(e.target.value)}
            className="w-[90px] h-9" data-testid="error-retention-days-input" />
          <span className="text-gray-500">days</span>
          <Button size="sm" onClick={save} disabled={saving} data-testid="error-retention-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </Button>
        </>
      ) : (
        <span className="font-medium" data-testid="error-retention-readonly">{label(cfg.error_retention_days || 90)}</span>
      )}
    </div>
  );
};

export default ErrorRetentionSettings;
